/**
 * Scope Utilities
 *
 * Helper functions for OAuth2 CRUDS scope management.
 * Follows the backend pattern from rbac_scopes.py
 *
 * CRUDS notation:
 * - c = Create (POST)
 * - r = Read (GET)
 * - u = Update (PUT/PATCH)
 * - d = Delete (DELETE)
 * - s = Special/admin operations
 */

/**
 * Parse OAuth2 scope string into resource permissions object.
 *
 * @param {string} scopeString - Space-separated scopes (e.g., "vcx_event.cru patient.r")
 * @returns {Object} - { resource_name: cruds_string }
 *
 * Example:
 *   parseScopeString("vcx_event.cru patient.r")
 *   => { vcx_event: 'cru', patient: 'r' }
 */
export const parseScopeString = (scopeString) => {
  if (!scopeString || typeof scopeString !== 'string') {
    return {};
  }

  const permissions = {};
  const scopes = scopeString.trim().split(/\s+/);

  scopes.forEach(scope => {
    if (scope.includes('.')) {
      const [resource, cruds] = scope.split('.', 2);
      if (resource && cruds) {
        permissions[resource] = cruds;
      }
    }
  });

  return permissions;
};

/**
 * Build OAuth2 scope string from resource permissions object.
 *
 * @param {Object} resourcePermissions - { resource_name: cruds_string }
 * @returns {string} - Space-separated scope string
 *
 * Example:
 *   buildScopeString({ vcx_event: 'cru', patient: 'r' })
 *   => "patient.r vcx_event.cru" (sorted alphabetically)
 */
export const buildScopeString = (resourcePermissions) => {
  if (!resourcePermissions || typeof resourcePermissions !== 'object') {
    return '';
  }

  const scopes = [];

  Object.entries(resourcePermissions).forEach(([resource, cruds]) => {
    if (cruds && cruds.length > 0) {
      scopes.push(`${resource}.${cruds}`);
    }
  });

  return scopes.sort().join(' ');
};

/**
 * Check if user has CRUDS permission for resource.operation.
 *
 * @param {Array<string>} userScopes - Array of scope strings (e.g., ['vcx_event.cru', 'patient.r'])
 * @param {string} resource - Resource name (e.g., 'vcx_event')
 * @param {string} operation - CRUDS operation (c/r/u/d/s)
 * @returns {boolean} - True if user has permission
 *
 * Example:
 *   hasPermission(['vcx_event.cru', 'patient.r'], 'vcx_event', 'u') => true
 *   hasPermission(['vcx_event.cru', 'patient.r'], 'vcx_event', 'd') => false
 */
export const hasPermission = (userScopes, resource, operation) => {
  if (!Array.isArray(userScopes) || !resource || !operation) {
    return false;
  }

  for (const scope of userScopes) {
    if (scope.includes('.')) {
      const [scopeResource, scopeOps] = scope.split('.', 2);
      if (scopeResource === resource && scopeOps.includes(operation)) {
        return true;
      }
    }
  }

  return false;
};

/**
 * Map HTTP method to CRUDS operation.
 *
 * @param {string} method - HTTP method (GET, POST, PUT, PATCH, DELETE)
 * @returns {string|null} - CRUDS operation (c/r/u/d) or null
 */
export const methodToCruds = (method) => {
  const METHOD_MAP = {
    'POST': 'c',
    'GET': 'r',
    'PUT': 'u',
    'PATCH': 'u',
    'DELETE': 'd'
  };

  return METHOD_MAP[method?.toUpperCase()] || null;
};

/**
 * Check if user can perform HTTP method on resource.
 *
 * @param {Array<string>} userScopes - Array of scope strings
 * @param {string} resource - Resource name
 * @param {string} method - HTTP method
 * @returns {boolean} - True if user has permission
 */
export const canPerformMethod = (userScopes, resource, method) => {
  const operation = methodToCruds(method);
  if (!operation) {
    return false;
  }

  return hasPermission(userScopes, resource, operation);
};

/**
 * Get common CRUDS permission presets.
 * Matches backend get_cruds_presets() from rbac_scopes.py
 *
 * @returns {Array} - Array of preset objects
 */
export const getCrudsPresets = () => {
  return [
    { value: 'cruds', label: 'Full Access (CRUDS)', description: 'Create, Read, Update, Delete, Special/Admin' },
    { value: 'crud', label: 'Standard (CRUD)', description: 'Create, Read, Update, Delete' },
    { value: 'cru', label: 'Staff (CRU)', description: 'Create, Read, Update (no delete)' },
    { value: 'cr', label: 'Limited (CR)', description: 'Create, Read only' },
    { value: 'ru', label: 'Edit Only (RU)', description: 'Read, Update (no create/delete)' },
    { value: 'r', label: 'Read-Only (R)', description: 'View only' },
    { value: '', label: 'No Access', description: 'No permissions for this resource' },
  ];
};

/**
 * Get human-readable label for CRUDS string.
 *
 * @param {string} cruds - CRUDS string (e.g., 'cru')
 * @returns {string} - Human-readable label
 */
export const getCrudsLabel = (cruds) => {
  const presets = getCrudsPresets();
  const preset = presets.find(p => p.value === cruds);
  return preset ? preset.label : cruds?.toUpperCase() || 'No Access';
};

/**
 * Validate scope string format.
 *
 * @param {string} scopeString - Scope string to validate
 * @returns {Object} - { valid: boolean, errors: string[] }
 */
export const validateScopeString = (scopeString) => {
  const errors = [];

  if (!scopeString || scopeString.trim() === '') {
    return { valid: true, errors: [] }; // Empty is valid
  }

  const scopes = scopeString.trim().split(/\s+/);

  scopes.forEach((scope, idx) => {
    if (!scope.includes('.')) {
      errors.push(`Scope ${idx + 1}: "${scope}" must include resource.cruds format`);
      return;
    }

    const [resource, cruds] = scope.split('.', 2);

    if (!resource) {
      errors.push(`Scope ${idx + 1}: Missing resource name`);
    }

    if (!cruds) {
      errors.push(`Scope ${idx + 1}: Missing CRUDS operations`);
    } else {
      // Validate CRUDS characters
      const validChars = 'cruds';
      for (const char of cruds) {
        if (!validChars.includes(char)) {
          errors.push(`Scope ${idx + 1}: Invalid CRUDS character "${char}" (must be c/r/u/d/s)`);
        }
      }
    }
  });

  return {
    valid: errors.length === 0,
    errors
  };
};

/**
 * Extract scopes from JWT token.
 *
 * @param {string} token - JWT token
 * @returns {Array<string>} - Array of scope strings
 */
export const extractScopesFromToken = (token) => {
  if (!token) {
    return [];
  }

  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    const payload = JSON.parse(jsonPayload);

    // OAuth2 scopes are space-separated string in 'scope' claim
    const scopeString = payload.scope || '';
    return scopeString.split(' ').filter(s => s.length > 0);
  } catch (error) {
    console.error('Error extracting scopes from token:', error);
    return [];
  }
};

/**
 * Check if user is admin/ankr (unrestricted access).
 *
 * @param {string} tokenType - Token type from JWT
 * @returns {boolean} - True if unrestricted
 */
export const isUnrestrictedAccess = (tokenType) => {
  return ['core_server_admin', 'core_server_general', 'core_server_ankr_user'].includes(tokenType);
};
